// Build -> simulate -> (partial sign) -> Phantom sign -> send -> confirm by polling.
import { ComputeBudgetProgram, PublicKey, TransactionMessage, VersionedTransaction } from "@solana/web3.js";
import { utils } from "@coral-xyz/anchor";
import { connection } from "./chain.js";
import { CONFIG } from "./config.js";
import { signTx, walletKey } from "./wallet.js";
import { TxError, describeTxErr, describeError } from "./errors.js";

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
const MAX_CU = 1_400_000;

async function priorityMicroLamports(writable) {
  const floor = CONFIG.cluster === "mainnet-beta" ? 20_000 : 1_000;
  try {
    const fees = await connection.getRecentPrioritizationFees({ lockedWritableAccounts: writable.slice(0, 128) });
    const vals = fees.map((f) => f.prioritizationFee).filter((v) => v > 0).sort((a, b) => a - b);
    if (!vals.length) return floor;
    const p75 = vals[Math.floor(vals.length * 0.75)];
    return Math.min(Math.max(p75, floor), 2_000_000);
  } catch {
    return floor;
  }
}

function compile(payer, blockhash, ixs, cuLimit, cuPrice) {
  const msg = new TransactionMessage({
    payerKey: payer,
    recentBlockhash: blockhash,
    instructions: [
      ComputeBudgetProgram.setComputeUnitLimit({ units: cuLimit }),
      ComputeBudgetProgram.setComputeUnitPrice({ microLamports: cuPrice }),
      ...ixs,
    ],
  }).compileToV0Message();
  return new VersionedTransaction(msg);
}

const sameBytes = (a, b) => a.length === b.length && a.every((v, i) => v === b[i]);

/**
 * Send instructions as ONE transaction. Phantom is asked for exactly one signature (the fee payer);
 * any extra Keypairs (e.g. the new mint) sign locally before Phantom.
 * @param {object} p
 * @param {import('@solana/web3.js').TransactionInstruction[]} p.ixs
 * @param {import('@solana/web3.js').Keypair[]} [p.signers]
 * @param {number} [p.minCu]  lower bound for the compute unit limit
 * @param {(stage:string, info?:object)=>void} [p.onStage]
 * @returns {Promise<{signature:string, logs?:string[]}>}
 */
export async function sendInstructions({ ixs, signers = [], minCu = 0, onStage = () => {} }) {
  const payer = walletKey();
  if (!payer) throw new TxError("Connect your Phantom wallet first.");

  onStage("preparing");
  const writable = [...new Set(ixs.flatMap((ix) => ix.keys.filter((k) => k.isWritable).map((k) => k.pubkey.toBase58())))].map(
    (s) => new PublicKey(s)
  );
  const [{ blockhash, lastValidBlockHeight }, cuPrice] = await Promise.all([
    connection.getLatestBlockhash("confirmed"),
    priorityMicroLamports(writable),
  ]);

  // 1) simulate with a generous limit to learn the real CU usage and catch errors before the wallet pops up
  onStage("simulating");
  const simTx = compile(payer, blockhash, ixs, MAX_CU, cuPrice);
  let sim;
  try {
    sim = await connection.simulateTransaction(simTx, { sigVerify: false, replaceRecentBlockhash: true, commitment: "confirmed" });
  } catch (e) {
    throw new TxError(describeError(e));
  }
  if (sim.value.err) {
    console.warn("simulation failed", sim.value.err, sim.value.logs);
    throw new TxError(describeTxErr(sim.value.err, sim.value.logs), { logs: sim.value.logs });
  }
  const used = sim.value.unitsConsumed || 200_000;
  const cuLimit = Math.min(MAX_CU, Math.max(minCu, Math.ceil(used * 1.25) + 15_000));

  // 2) final transaction; local signers first (partial), then Phantom
  const tx = compile(payer, blockhash, ixs, cuLimit, cuPrice);
  if (signers.length) tx.sign(signers);
  const before = tx.message.serialize();
  onStage("signing");
  let signed;
  try {
    signed = await signTx(tx);
  } catch (e) {
    throw new TxError(describeError(e));
  }
  // If the wallet altered the message (e.g. added its own instructions), our local signatures are stale: re-sign.
  if (signers.length && !sameBytes(signed.message.serialize(), before)) signed.sign(signers);

  const raw = signed.serialize();
  const signature = utils.bytes.bs58.encode(signed.signatures[0]);
  onStage("sending", { signature });

  // 3) send + rebroadcast while polling getSignatureStatuses (no websocket subscription needed)
  const send = () => connection.sendRawTransaction(raw, { skipPreflight: true, maxRetries: 0 }).catch(() => null);
  try {
    await connection.sendRawTransaction(raw, { skipPreflight: true, maxRetries: 0 });
  } catch (e) {
    throw new TxError(describeError(e), { signature });
  }
  onStage("confirming", { signature });
  const started = Date.now();
  let lastSend = Date.now();
  let n = 0;
  for (;;) {
    await sleep(1200);
    n++;
    let st = null;
    try {
      st = (await connection.getSignatureStatuses([signature], { searchTransactionHistory: false })).value[0];
    } catch {
      /* transient RPC error, keep polling */
    }
    if (st) {
      if (st.err) {
        let logs;
        try {
          const t = await connection.getTransaction(signature, { commitment: "confirmed", maxSupportedTransactionVersion: 0 });
          logs = t?.meta?.logMessages;
        } catch {
          /* ignore */
        }
        throw new TxError(describeTxErr(st.err, logs), { signature, logs });
      }
      if (st.confirmationStatus === "confirmed" || st.confirmationStatus === "finalized") {
        onStage("confirmed", { signature });
        return { signature };
      }
    }
    if (Date.now() - lastSend > 2500) {
      lastSend = Date.now();
      send();
    }
    if (n % 4 === 0) {
      try {
        const height = await connection.getBlockHeight("confirmed");
        if (height > lastValidBlockHeight) {
          // one last look before giving up
          const fin = (await connection.getSignatureStatuses([signature], { searchTransactionHistory: true })).value[0];
          if (fin && !fin.err) return { signature };
          throw new TxError("The transaction expired before it landed. Nothing was charged. Please try again.", { signature });
        }
      } catch (e) {
        if (e instanceof TxError) throw e;
      }
    }
    if (Date.now() - started > 120_000) {
      throw new TxError("Still not confirmed after 2 minutes. Check the explorer link before retrying.", { signature });
    }
  }
}
